import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Field, Form, Formik } from "formik";
import React, { FC, useState } from "react";
import Modal from 'react-modal';
import { useLocale } from "../../contexts/localeContext";
import { TranslationKeys } from "../../contexts/TranslationKeys";
import closeIcon from '../../images/close.svg';
import Label from "../label/label";
import {getErrorMessageToShow, initialValues} from "@/utilities/passwordUtil.tsx";
import {convertTextPwdToBase64Pwd, requiredValidation, validateConfirmPassword, validateDevicePassword, validatePassword} from "@/utilities/Validation.ts";
import {useDialog} from "@/components/dialog/DialogContext.tsx";
import {DialogContent} from "@/components/dialog/Dialog.tsx";
import {ButtonTypes} from "@/models/ButtonTypes.ts";
import {ExecuteCommand} from "@/models/ExecuteCommand.ts";
import Commands from "@/constants/commands.ts";
import {HardwareSubCmds} from "@/constants/sub-commands.ts";
import {executeCommand} from "@/services/executeCommandService.ts";
import {getErrorMessage} from "@/services/utilities/ServiceError.ts";
import {COMMAND_STATE} from "@/constants/command-status.ts";
import {useSelector} from "react-redux";
import {IAppState} from "@/store/types.ts";
import {useGlobalState} from "@/Providers.tsx";

interface ChangeDevicePasswordModalProps {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    onChangePassword: (values: any) => void;
    hideCurrentPassword: boolean;
    enforceDefPwdChange: boolean;
    resyncPwd: boolean;
    macAddress: string;
}

const ChangePasswordModal: FC<ChangeDevicePasswordModalProps> = ({ isOpen, setIsOpen, onChangePassword, hideCurrentPassword, enforceDefPwdChange, resyncPwd, macAddress}) => {
    const { translate } = useLocale();
    const [showPassword, setShowPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const dialog = useDialog();
    const user = useSelector((state: IAppState) => (state.userInfo));
    const { buildInfo } = useGlobalState() ?? {};

    const getSecurityNoticeMessage = () => {
        if (enforceDefPwdChange) {
            return translate.byKey(TranslationKeys.defaultPasswordMustBeChanged);
        }
        else if (resyncPwd) {
            return translate.byKey(TranslationKeys.resyncControllerPassword);
        }
    }

    const showDialogMessage = (message: string) => {
        const dialogContent: DialogContent = {
            title: translate.byKey(TranslationKeys.securityPolicyPassword),
            message: message,
            buttons: [
                {
                    id: "changeDevicePasswordId",
                    text: translate.byKey(TranslationKeys.ok),
                    onClick: () => {},
                    type: ButtonTypes.RedRoundedButton
                }
            ]
        }
        dialog.pushError(dialogContent);
    }

    // Will send the new password down to the backend to validate against our device password minimum requirements.
    const verifyDevicePassword  = async (password: string, confirmPassword: string) => {
        try {
            const command: ExecuteCommand = {
                cmd: Commands.Device.label,
                cmdSubType: HardwareSubCmds.VERIFY_DEVICE_PASSWORD,
                userLoginId: user.id,
                password: convertTextPwdToBase64Pwd(password) ?? undefined,
                confirmPassword: convertTextPwdToBase64Pwd(confirmPassword) ?? undefined,
                macAddress: macAddress,
                jsonResponse: ""
            }
            const response = await executeCommand(command);
            return response;
        } catch (error) {
            showDialogMessage(getErrorMessage(error));
        }
    }

    const onSubmitPassword = async (values) => {
        if (!(!!buildInfo?.isEnforceDefPwdValidation) || resyncPwd) {
            onChangePassword(values); // There is no validation needed for this password just change.
            return;
        }

        // We must validate the password before changing it.
        const checkPasswordResult = await verifyDevicePassword(values.newPassword, values.confirmPassword);
        if (checkPasswordResult) {
            if (checkPasswordResult.status !== COMMAND_STATE.COMMAND_COMPLETE) {
                showDialogMessage(checkPasswordResult.message)
                return;
            }
            onChangePassword(values);
        }
    }

    return <Modal
        isOpen={isOpen}
        ariaHideApp={false}
        overlayClassName="flex w-screen position-float fixed top-0 left-0 h-screen bg-opacity-75 bg-white"
        className="bg-white border-none m-auto shadow select-none outline-none overflow-hidden">
        <Formik
            enableReinitialize={true}
            initialValues={initialValues}
            onSubmit={onSubmitPassword}>
            {
                ({ errors, values, touched }) => {
                    return <Form>
                        <div className={`w-full p-2 border-b flex flex-row items-center bg-sidemenu-focus text-white justify-between`}>
                            <div className="flex flex-row">
                                <p className="pl-3 font-bold font text-white">{translate.byKey(TranslationKeys.changePassword)}</p>
                            </div>
                            <img src={closeIcon} onClick={() => setIsOpen(false)} className="cursor-pointer mr-2" />
                        </div>
                        {(enforceDefPwdChange || resyncPwd) && (
                            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 mx-4 mt-3">
                                <div className="flex">
                                    <div className="ml-2">
                                        <p className="text-sm text-yellow-700 mb-2 break-words">
                                            <strong>{translate.byKey(TranslationKeys.securityNotice)}</strong>
                                        </p>
                                        <p className="text-sm text-yellow-700 break-words whitespace-normal max-w-xs">
                                            {getSecurityNoticeMessage()}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className="pt-3 pl-4 pr-0" style={{ width: '350px' }}>
                            { !hideCurrentPassword &&
                                <div className="flex flex-col flex-1 pr-3">
                                    <Label htmlFor='currentPassword' className="text-xs text-gray-600 mt-1" required>
                                        {translate.byKey(TranslationKeys.password)}:
                                    </Label>
                                    <div className="flex flex-row justify-between">
                                        <Field
                                            name="currentPassword"
                                            type={showPassword ? 'text' : 'password'}
                                            validate={(value) => requiredValidation(value, translate)}
                                            className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-800 text-sm bg-gray-input w-full" />
                                        <div className="flex relative w-6 ml-1">
                                            <FontAwesomeIcon
                                                className="absolute bottom-0 mb-1 right-0 cursor-pointer"
                                                onClick={() => setShowPassword(showPassword => !showPassword)}
                                                icon={showPassword ? faEye : faEyeSlash} />
                                        </div>
                                    </div>
                                </div>
                            }
                            {/* New Password */}
                            { !resyncPwd && (
                                <div className="flex flex-col flex-1 pr-3 mt-2">
                                    <Label htmlFor='newPassword' className="text-xs text-gray-600 mt-1" required>
                                        {translate.byKey(TranslationKeys.newPassword)}:
                                    </Label>
                                    <div className="flex flex-row justify-between">
                                        <Field
                                            name="newPassword"
                                            type={showNewPassword ? 'text' : 'password'}
                                            validate={(value) => validateDevicePassword(value, translate, true)}
                                            className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-800 text-sm bg-gray-input w-full" />
                                        <div className="flex relative w-6 ml-1">
                                            <FontAwesomeIcon
                                                className="absolute bottom-0 mb-1 right-0 cursor-pointer"
                                                onClick={() => setShowNewPassword(showNewPassword => !showNewPassword)}
                                                icon={showNewPassword ? faEye : faEyeSlash} />
                                        </div>
                                    </div>
                                </div>
                            )}
                            {/* Current Password */}
                            { !resyncPwd && (
                                <div className="flex flex-col flex-1 pr-3 mt-2">
                                    <Label htmlFor='confirmPassword' className="text-xs text-gray-600 mt-1" required>
                                        {translate.byKey(TranslationKeys.confirmPassword)}:
                                    </Label>
                                    <div className="flex flex-row justify-between">
                                        <Field
                                            name="confirmPassword"
                                            type={showConfirmPassword ? 'text' : 'password'}
                                            validate={(value) => validateConfirmPassword(values.newPassword, value, translate)}
                                            className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-800 text-sm bg-gray-input w-full" />
                                        <div className="flex relative w-6 ml-1">
                                            <FontAwesomeIcon
                                                className="absolute bottom-0 mb-1 right-0 cursor-pointer"
                                                onClick={() => setShowConfirmPassword(showConfirmPassword => !showConfirmPassword)}
                                                icon={showConfirmPassword ? faEye : faEyeSlash} />
                                        </div>
                                    </div>
                                </div>
                            )}
                            {getErrorMessageToShow(errors, touched)}
                        </div>
                        <div className="flex flex-row pb-3 justify-end bg-white">
                            <div className="flex flex-row mt-4 mr-5">
                                <button id='submitBtn' className="secondary secondary-enabled py-1 px-5" type='submit'>
                                    {translate.byKey(TranslationKeys.submit)}
                                </button>
                            </div>
                        </div>
                    </Form>
                }
            }
        </Formik>
    </Modal>
}

export default ChangePasswordModal;
